import React, { useState } from 'react';
import {
  Card, Table, Tag, Button, Space, Tabs, Popconfirm, message,
  Input, Select, DatePicker, Typography
} from 'antd';
import { SwapRightOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import InitiateDrawer from './InitiateModal';
import TransferDetailDrawer from './TransferDetailDrawer';
import { mockOutTasks, mockInTasks } from './mockData';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ══════════════════════════════════════════════
//  状态标签配置
// ══════════════════════════════════════════════
const OUT_STATUS = {
  pending:   { text: '待对方确认', color: 'orange' },
  completed: { text: '对方已接收', color: 'green' },
  rejected:  { text: '对方已拒绝', color: 'red' },
  revoked:   { text: '我已撤销',   color: 'default' },
};

const IN_STATUS = {
  pending_receive: { text: '待我接收',   color: 'orange' },
  received:        { text: '我已接收',   color: 'green' },
  rejected:        { text: '我已拒绝',   color: 'red' },
  revoked_by_other:{ text: '对方已撤销', color: 'default' },
};

// ══════════════════════════════════════════════
//  主组件：DeviceTransfer
// ══════════════════════════════════════════════
const DeviceTransfer = () => {
  const [activeTab, setActiveTab] = useState('initiated');

  // 弹窗/抽屉控制
  const [modalVisible, setModalVisible] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // 任务列表状态（用于模拟撤销/接收/拒绝操作）
  const [outTasks, setOutTasks] = useState(mockOutTasks);
  const [inTasks, setInTasks] = useState(mockInTasks);

  // 筛选状态
  const [outFilters, setOutFilters] = useState({ license: '', taskId: '', status: [], dateRange: [] });
  const [inFilters, setInFilters] = useState({ license: '', taskId: '', status: [], dateRange: [] });

  // ════ 操作处理 ════
  const handleRevoke = (taskId) => {
    setOutTasks(prev =>
      prev.map(t => t.id === taskId ? { ...t, status: 'revoked' } : t)
    );
    message.success(`转移任务 ${taskId} 已撤销，设备已解锁`);
  };

  const handleConfirm = (taskId) => {
    setInTasks(prev =>
      prev.map(t => t.id === taskId ? { ...t, status: 'received' } : t)
    );
    message.success(`已确认接收转移任务 ${taskId}，设备归入名下，关联产品已建立`);
  };

  const handleReject = (taskId) => {
    setInTasks(prev =>
      prev.map(t => t.id === taskId ? { ...t, status: 'rejected' } : t)
    );
    message.warning(`已拒绝转移任务 ${taskId}，设备已退回`);
  };

  const showDetail = (record) => {
    setSelectedRecord(record);
    setDrawerOpen(true);
  };

  // ════ 筛选逻辑 ════
  const filterTasks = (tasks, filters, statusMap) => {
    return tasks.filter(t => {
      // License 搜索：在 licenses 数组中查找
      if (filters.license) {
        const match = (t.licenses || []).some(lic =>
          lic.license.toLowerCase().includes(filters.license.toLowerCase())
        );
        if (!match) return false;
      }
      // 任务单号搜索
      if (filters.taskId && !t.id.toLowerCase().includes(filters.taskId.toLowerCase())) {
        return false;
      }
      // 状态筛选
      if (filters.status && filters.status.length > 0 && !filters.status.includes(t.status)) {
        return false;
      }
      // 日期范围筛选
      if (filters.dateRange && filters.dateRange.length === 2) {
        const taskDate = new Date(t.createTime);
        if (taskDate < filters.dateRange[0] || taskDate > filters.dateRange[1]) {
          return false;
        }
      }
      return true;
    });
  };

  const filteredOut = filterTasks(outTasks, outFilters, OUT_STATUS);
  const filteredIn = filterTasks(inTasks, inFilters, IN_STATUS);

  // ════ 发起方 列表列 ════
  const outColumns = [
    {
      title: '任务单号',
      dataIndex: 'id',
      key: 'id',
      width: 170,
      render: (t) => <Text copyable style={{ fontFamily: 'monospace' }}>{t}</Text>,
    },
    { title: '发起时间', dataIndex: 'createTime', key: 'createTime', width: 170 },
    {
      title: '转移产品',
      key: 'product',
      width: 200,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.productName}</div>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{r.productId}</Text>
        </div>
      ),
    },
    { title: '设备数量', dataIndex: 'deviceCount', key: 'deviceCount', width: 90 },
    {
      title: '接收企业',
      key: 'target',
      width: 180,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.targetEnterprise}</div>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{r.targetEnterpriseId}</Text>
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (s) => {
        const cfg = OUT_STATUS[s];
        return <Tag color={cfg?.color} style={{ border: 'none' }}>{cfg?.text || s}</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      fixed: 'right',
      render: (_, r) => (
        <Space size={0} split={<span style={{ color: '#d9d9d9', margin: '0 4px' }}>|</span>}>
          <Button type="link" size="small" onClick={() => showDetail(r)}>查看明细</Button>
          {r.status === 'pending' && (
            <Popconfirm
              title="确定撤销此次转移？"
              description="撤销后设备将解锁，可再次转移。"
              onConfirm={() => handleRevoke(r.id)}
              okText="确认撤销"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button type="link" size="small" danger>撤销</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // ════ 接收方 列表列 ════
  const inColumns = [
    {
      title: '任务单号',
      dataIndex: 'id',
      key: 'id',
      width: 170,
      render: (t) => <Text copyable style={{ fontFamily: 'monospace' }}>{t}</Text>,
    },
    { title: '发起时间', dataIndex: 'createTime', key: 'createTime', width: 170 },
    {
      title: '关联产品',
      key: 'product',
      width: 200,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.productName}</div>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{r.productId}</Text>
        </div>
      ),
    },
    { title: '设备数量', dataIndex: 'deviceCount', key: 'deviceCount', width: 90 },
    {
      title: '来源企业',
      key: 'source',
      width: 180,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.sourceEnterprise}</div>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{r.sourceEnterpriseId}</Text>
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (s) => {
        const cfg = IN_STATUS[s];
        return <Tag color={cfg?.color} style={{ border: 'none' }}>{cfg?.text || s}</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, r) => (
        <Space size={0} split={<span style={{ color: '#d9d9d9', margin: '0 4px' }}>|</span>}>
          <Button type="link" size="small" onClick={() => showDetail(r)}>查看设备清单</Button>
          {r.status === 'pending_receive' && (
            <>
              <Popconfirm
                title="确认接收这批设备？"
                description="接收后设备将归入你名下，可进行管理。"
                onConfirm={() => handleConfirm(r.id)}
                okText="确认接收"
                cancelText="取消"
              >
                <Button type="link" size="small" style={{ color: '#52c41a' }}>确认接收</Button>
              </Popconfirm>
              <Popconfirm
                title="确定拒绝接收？"
                description="设备将退回发起方，对方可再次转移。"
                onConfirm={() => handleReject(r.id)}
                okText="确认拒绝"
                cancelText="取消"
                okButtonProps={{ danger: true }}
              >
                <Button type="link" size="small" danger>拒绝</Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  // ════ 筛选区渲染 ════
  const renderFilters = (filters, setFilters, statusOptions) => (
    <div style={{ marginBottom: 20, marginTop: 16 }}>
      <Space size="middle" wrap>
        <Input
          placeholder="License 搜索"
          style={{ width: 180 }}
          value={filters.license}
          onChange={(e) => setFilters(prev => ({ ...prev, license: e.target.value }))}
          allowClear
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
        />
        <Input
          placeholder="任务单号"
          style={{ width: 180 }}
          value={filters.taskId}
          onChange={(e) => setFilters(prev => ({ ...prev, taskId: e.target.value }))}
          allowClear
        />
        <Select
          mode="multiple"
          placeholder="状态筛选"
          style={{ minWidth: 180 }}
          value={filters.status}
          onChange={(val) => setFilters(prev => ({ ...prev, status: val }))}
          allowClear
          options={statusOptions}
        />
        <RangePicker
          placeholder={['开始时间', '结束时间']}
          value={filters.dateRange}
          onChange={(val) => setFilters(prev => ({ ...prev, dateRange: val || [] }))}
        />
        <Space>
          <Button type="primary" icon={<SearchOutlined />}>查询</Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => setFilters({ license: '', taskId: '', status: [], dateRange: [] })}
          >
            重置
          </Button>
        </Space>
      </Space>
    </div>
  );

  // ════ Tab 配置 ════
  const tabItems = [
    {
      key: 'initiated',
      label: '由我发起（转出）',
      children: (
        <>
          {renderFilters(outFilters, setOutFilters, Object.entries(OUT_STATUS).map(([k, v]) => ({
            value: k,
            label: v.text,
          })))}
          <Table
            columns={outColumns}
            dataSource={filteredOut}
            rowKey="id"
            scroll={{ x: 1100 }}
            pagination={{
              showSizeChanger: true,
              showTotal: (t) => `共 ${t} 条转出记录`,
              defaultPageSize: 10,
            }}
            locale={{ emptyText: '暂无转出记录' }}
          />
        </>
      ),
    },
    {
      key: 'received',
      label: '待我处理（转入）',
      children: (
        <>
          {renderFilters(inFilters, setInFilters, Object.entries(IN_STATUS).map(([k, v]) => ({
            value: k,
            label: v.text,
          })))}
          <Table
            columns={inColumns}
            dataSource={filteredIn}
            rowKey="id"
            scroll={{ x: 1100 }}
            pagination={{
              showSizeChanger: true,
              showTotal: (t) => `共 ${t} 条转入记录`,
              defaultPageSize: 10,
            }}
            locale={{ emptyText: '暂无待处理的转移' }}
          />
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={4}>设备调拨</Title>
      </div>

      <Card bordered={false} bodyStyle={{ padding: '16px 24px 24px' }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          tabBarExtraContent={
            activeTab === 'initiated' ? (
              <Button
                type="primary"
                icon={<SwapRightOutlined />}
                onClick={() => setModalVisible(true)}
              >
                发起转移
              </Button>
            ) : null
          }
          tabBarStyle={{ marginBottom: 0 }}
        />
      </Card>

      {/* 发起转移弹窗 */}
      <InitiateDrawer
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSuccess={() => setModalVisible(false)}
      />

      {/* 转移明细抽屉 */}
      <TransferDetailDrawer
        open={drawerOpen}
        record={selectedRecord}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedRecord(null);
        }}
      />
    </div>
  );
};

export default DeviceTransfer;
